import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { techniciansApi, workOrdersApi } from "../lib/api";
import { firstLine, formatDate, formatMoney, formatWorkOrderNumber } from "../lib/money";
import { useShopType } from "../lib/theme";
import { Spinner } from "../components/ui-modern";
import {
  Eyebrow,
  ModernButton,
  ModernEmptyState,
  ModernErrorBanner,
  ModernInfoTip,
  ModernPagination,
  ModernSearchInput,
  ModernStatusPill,
  ModernTableCard,
  ModernTd,
  ModernTh,
} from "../components/ui-modern";

const STATUS_HELP =
  "Each row shows work order progress on the left and billing status on the right — a work order becomes billed the moment it's invoiced, so its progress badge shows \"Completed\" once that happens rather than freezing on \"Invoiced.\" The billing badge shows whether it's been invoiced at all, and if so, its payment status (Not Paid, Partially Paid, Paid in Full, or Void if the invoice itself was voided). Filtering by \"Completed\" includes already-invoiced work orders for the same reason — use the invoice status filter for billing state specifically.";

// Same values as WorkOrdersListPageClassic.tsx's own <Select> options —
// rendered here as two rows of filter PILLS instead (one for work order
// stage, one for billing status), matching InvoicesListPageModern's own
// INVOICE_FILTERS pattern (and, ultimately, the mockup's filter-pill
// row). Tone per pill is a private duplicate of ui-modern.tsx's own
// MODERN_STATUS_TONE map (not exported) so each pill's color always
// matches the ModernStatusPill shown in that same row's Stage/Billing
// column.
const STATUS_FILTERS: { value: string; label: string; tone: { bg: string; fg: string } }[] = [
  { value: "", label: "All Work Order Statuses", tone: { bg: "#eef1f6", fg: "#4a5361" } },
  { value: "estimate", label: "Estimate", tone: { bg: "#eef1f6", fg: "#4a5361" } },
  { value: "approved", label: "Approved", tone: { bg: "#e6f1f5", fg: "#0b5468" } },
  { value: "in_progress", label: "In Progress", tone: { bg: "#fdf3e0", fg: "#8a5c00" } },
  { value: "complete", label: "Completed", tone: { bg: "#e7f4ea", fg: "#1a6b2c" } },
  { value: "invoiced", label: "Invoiced", tone: { bg: "#e7f4ea", fg: "#1a6b2c" } },
  { value: "void", label: "Void", tone: { bg: "#fdecea", fg: "#b3261e" } },
];

type SortColumn = "openedAt" | "customerName" | "status" | "invoiceStatus" | "total";
type SortDirection = "asc" | "desc";
interface SortState {
  column: SortColumn;
  direction: SortDirection;
}
const DEFAULT_SORT: SortState = { column: "openedAt", direction: "desc" };

const INVOICE_STATUS_FILTERS: { value: string; label: string; tone: { bg: string; fg: string } }[] = [
  { value: "", label: "All Billing Statuses", tone: { bg: "#eef1f6", fg: "#4a5361" } },
  { value: "not_invoiced", label: "Not Invoiced", tone: { bg: "#f1f4f9", fg: "#6b7a8f" } },
  { value: "open", label: "Not Paid", tone: { bg: "#fdf3e0", fg: "#8a5c00" } },
  { value: "partially_paid", label: "Partially Paid", tone: { bg: "#e6f1f5", fg: "#0b5468" } },
  { value: "paid", label: "Paid in Full", tone: { bg: "#e7f4ea", fg: "#1a6b2c" } },
  { value: "void", label: "Invoice Void", tone: { bg: "#fdecea", fg: "#b3261e" } },
];

/**
 * The modern theme's Work Orders list — see docs/FRONTEND_BACKLOG.md
 * Round 60 and `Bay2Bill Redesign.dc.html` lines ~196-257 ("Work orders").
 *
 * Same data/mutations as WorkOrdersListPageClassic.tsx — same query key,
 * same filter/pagination state shape, same `technicianId` search-param
 * banner — duplicated here as a thin re-run of the same `useQuery` calls
 * (matching the DashboardPage/DashboardPageModern precedent) rather than
 * extracted into a shared hook, since neither page has any mutation of
 * its own to keep in sync, just a read query.
 *
 * The mockup's table shows one "Billing" pill per row; Classic shows
 * *two* badges when a work order is invoiced (an "Invoiced" marker plus
 * the invoice's own payment-status badge). Rather than crowd two pills
 * into the mockup's single Billing column, this shows just the payment
 * status pill (defaulting to "Not Invoiced" when there is none yet) —
 * the same information Classic shows, just consolidated into the one
 * column the redesign's layout has room for.
 */
export function WorkOrdersListPageModern() {
  const hasVehicles = useShopType() !== "detailing";
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const technicianId = searchParams.get("technicianId");
  const [search, setSearch] = useState("");
  // Seeded once from the URL (e.g. the Dashboard's "In the bays"/"Ready to
  // invoice" stat cards link here with `?status=...`) — same one-time-read
  // pattern as `technicianId` above, just via a lazy initializer since these
  // two are plain local state (the pill clicks below never write back to
  // the URL, unlike InvoicesListPageModern's own `status`).
  const [status, setStatus] = useState<string[]>(() => searchParams.getAll("status"));
  const [invoiceStatus, setInvoiceStatus] = useState<string[]>(() => searchParams.getAll("invoiceStatus"));
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);

  const { data, isLoading, error } = useQuery({
    queryKey: ["work-orders", "list", search, status, invoiceStatus, technicianId, sort, page, pageSize],
    queryFn: () =>
      workOrdersApi.list({
        search: search || undefined,
        status: status.length > 0 ? status : undefined,
        invoiceStatus: invoiceStatus.length > 0 ? invoiceStatus : undefined,
        technicianId: technicianId ? Number(technicianId) : undefined,
        includeVoid: true,
        sortBy: sort.column,
        sortDirection: sort.direction,
        limit: pageSize,
        offset: (page - 1) * pageSize,
      }),
  });

  function toggleSort(column: SortColumn): void {
    setSort((prev) => (prev.column === column ? { column, direction: prev.direction === "asc" ? "desc" : "asc" } : { column, direction: "asc" }));
    setPage(1);
  }

  // "All" (empty value) clears every selected pill in that group; any
  // other pill toggles itself on/off — clicking an already-active pill
  // again removes it (OR'd together with whatever else stays selected).
  function toggleStatus(value: string): void {
    setStatus((prev) => (value === "" ? [] : prev.includes(value) ? prev.filter((s) => s !== value) : [...prev, value]));
    setPage(1);
  }
  function toggleInvoiceStatus(value: string): void {
    setInvoiceStatus((prev) => (value === "" ? [] : prev.includes(value) ? prev.filter((s) => s !== value) : [...prev, value]));
    setPage(1);
  }
  const isDefaultSort = sort.column === DEFAULT_SORT.column && sort.direction === DEFAULT_SORT.direction;
  function sortIndicator(column: SortColumn): string {
    return sort.column === column ? (sort.direction === "asc" ? " ▲" : " ▼") : "";
  }
  const technicianQuery = useQuery({
    queryKey: ["technician", technicianId],
    queryFn: () => techniciansApi.get(Number(technicianId)),
    enabled: !!technicianId,
  });

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3.5">
        <div>
          <Eyebrow className="mb-2 text-[11px] text-[#0f6d86]">
            {data ? `${data.total} work order${data.total === 1 ? "" : "s"}` : "…"}
          </Eyebrow>
          <h1 className="m-0 flex items-center gap-2 text-[28px] font-bold text-[#071a33]">
            Work Orders <ModernInfoTip text={STATUS_HELP} below />
          </h1>
        </div>
        <Link to="/work-orders/new">
          <ModernButton>+ New work order</ModernButton>
        </Link>
      </div>

      {technicianId && (
        <div className="mb-4 flex items-center gap-3 rounded-[10px] border border-[#e2e7ee] bg-white px-4 py-3 text-[14px] text-[#101b2d]">
          Showing work orders for {technicianQuery.data?.technician.displayName ?? "this technician"}
          <button
            type="button"
            className="text-[13px] font-semibold text-[#0f6d86] underline"
            onClick={() => {
              setSearchParams({});
              setPage(1);
            }}
          >
            Clear
          </button>
        </div>
      )}

      <div className="mb-3.5 flex flex-wrap items-center gap-2.5">
        <ModernSearchInput
          className="max-w-[340px] flex-1"
          placeholder="Customer, tech, number, date…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          aria-label="Search work orders"
        />
        <ModernButton type="button" variant="secondary" onClick={() => setSort(DEFAULT_SORT)} disabled={isDefaultSort}>
          Reset Sort
        </ModernButton>
      </div>

      <div className="mb-2 flex flex-wrap gap-2" role="group" aria-label="Filter by stage">
        {STATUS_FILTERS.map((f) => {
          const active = f.value === "" ? status.length === 0 : status.includes(f.value);
          return (
            <button
              key={f.value}
              type="button"
              onClick={() => toggleStatus(f.value)}
              style={{ background: f.tone.bg, color: f.tone.fg, borderColor: active ? f.tone.fg : "transparent" }}
              className="h-[38px] rounded-full border-2 px-3.5 text-[13px] font-semibold"
            >
              {f.label}
            </button>
          );
        })}
      </div>
      <div className="mb-4 flex flex-wrap gap-2" role="group" aria-label="Filter by billing status">
        {INVOICE_STATUS_FILTERS.map((f) => {
          const active = f.value === "" ? invoiceStatus.length === 0 : invoiceStatus.includes(f.value);
          return (
            <button
              key={f.value}
              type="button"
              onClick={() => toggleInvoiceStatus(f.value)}
              style={{ background: f.tone.bg, color: f.tone.fg, borderColor: active ? f.tone.fg : "transparent" }}
              className="h-[38px] rounded-full border-2 px-3.5 text-[13px] font-semibold"
            >
              {f.label}
            </button>
          );
        })}
      </div>

      {isLoading && <Spinner />}
      {error && <ModernErrorBanner message="Could not load work orders." />}
      {data && data.items.length === 0 && <ModernEmptyState>No work orders match yet.</ModernEmptyState>}

      {data && data.items.length > 0 && (
        <ModernTableCard>
          <table className="w-full min-w-[720px] border-collapse text-[14px]">
            <thead>
              <tr className="bg-[#f8fafc]">
                <ModernTh onClick={() => toggleSort("openedAt")}>Work Order{sortIndicator("openedAt")}</ModernTh>
                <ModernTh>{hasVehicles ? "Vehicle & concern" : "Concern"}</ModernTh>
                <ModernTh onClick={() => toggleSort("status")}>Stage{sortIndicator("status")}</ModernTh>
                <ModernTh onClick={() => toggleSort("invoiceStatus")}>Billing{sortIndicator("invoiceStatus")}</ModernTh>
                <ModernTh align="right" onClick={() => toggleSort("total")}>
                  Total{sortIndicator("total")}
                </ModernTh>
              </tr>
            </thead>
            <tbody>
              {data.items.map((wo) => (
                <tr
                  key={wo.id}
                  onClick={() => navigate(`/work-orders/${wo.workOrderNumber}`)}
                  className="cursor-pointer transition-colors hover:bg-[#f1f7f9]"
                >
                  <ModernTd>
                    <Link
                      to={`/work-orders/${wo.workOrderNumber}`}
                      onClick={(e) => e.stopPropagation()}
                      className="font-semibold text-[#071a33] no-underline"
                    >
                      {wo.customerName}
                    </Link>
                    <div
                      className="mt-1 font-mono text-[11.5px] leading-tight text-[#8b97a8]"
                      style={{ fontFamily: "'Roboto Mono', ui-monospace, monospace" }}
                    >
                      {formatWorkOrderNumber(wo.workOrderNumber)} · {formatDate(wo.openedAt)}
                    </div>
                    {wo.technicianNames && <div className="mt-1 text-[12.5px] text-[#6b7a8f]">{wo.technicianNames}</div>}
                  </ModernTd>
                  <ModernTd>
                    {hasVehicles && (
                      <div className="text-[13.5px] text-[#101b2d]">{wo.vehicleDescription ? firstLine(wo.vehicleDescription) : "—"}</div>
                    )}
                    {wo.customerConcern ? (
                      <div className={hasVehicles ? "mt-1 text-[12.5px] text-[#6b7a8f]" : "text-[13.5px] text-[#101b2d]"}>{wo.customerConcern}</div>
                    ) : (
                      !hasVehicles && <div className="text-[13.5px] text-[#8b97a8]">—</div>
                    )}
                  </ModernTd>
                  <ModernTd>
                    <ModernStatusPill status={wo.status === "invoiced" ? "complete" : wo.status} />
                  </ModernTd>
                  <ModernTd>
                    <ModernStatusPill status={wo.invoiceStatus ?? "not_invoiced"} />
                  </ModernTd>
                  <ModernTd align="right" className="font-bold tabular-nums text-[#071a33]">
                    {formatMoney(wo.total)}
                  </ModernTd>
                </tr>
              ))}
            </tbody>
          </table>
          <ModernPagination
            page={page}
            pageSize={pageSize}
            total={data.total}
            onPageChange={setPage}
            onPageSizeChange={(n) => {
              setPageSize(n);
              setPage(1);
            }}
          />
        </ModernTableCard>
      )}
    </div>
  );
}
